/**
 * Public open signup — /signup?ref=CODE&promo=CODE
 */

import * as api from "./api.js";
import { clear, el, enhancePasswordFields, withShowPassword } from "./dom.js";
import { ApiError } from "./types.js";

export function isSignupPath(pathname: string = window.location.pathname): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/signup" || p === "/register";
}

function queryParams(): { ref: string; promo: string } {
  const q = new URLSearchParams(window.location.search);
  return {
    ref: (q.get("ref") ?? q.get("referral") ?? "").trim(),
    promo: (q.get("promo") ?? q.get("code") ?? "").trim(),
  };
}

export function mountSignupPage(container: HTMLElement): void {
  const params = queryParams();
  render(container, { step: "form", ref: params.ref, promo: params.promo });
}

type Step =
  | { step: "form"; ref: string; promo: string; busy?: boolean; error?: string }
  | {
      step: "confirm";
      email: string;
      ref: string;
      promo: string;
      busy?: boolean;
      error?: string;
      notice?: string;
    }
  | { step: "done"; email: string };

function render(root: HTMLElement, state: Step): void {
  clear(root);
  const page = el("div", { className: "join-page" });

  const top = el("header", { className: "join-topbar" });
  top.append(
    el(
      "a",
      { href: "/", className: "brand join-brand brand-link" },
      el("img", {
        className: "brand-mark",
        src: "/favicon.svg",
        alt: "",
        width: "28",
        height: "28",
      }),
      el("span", null, "MCP to AI"),
    ),
    el(
      "div",
      { className: "join-top-actions" },
      el("a", { href: "/", className: "btn-ghost btn-sm" }, "Sign in"),
    ),
  );
  page.append(top);

  const card = el("section", { className: "join-hero", style: "max-width:420px;margin:0 auto;" });

  if (state.step === "done") {
    card.append(
      el("h1", { className: "join-title" }, "You're verified"),
      el(
        "p",
        { className: "join-lede" },
        `Account ${state.email} is ready. Sign in to connect BYOK keys and subscribe.`,
      ),
      el(
        "a",
        { href: "/", className: "btn-primary", style: "display:inline-block;margin-top:16px;" },
        "Sign in",
      ),
    );
    page.append(card);
    root.append(page);
    return;
  }

  if (state.step === "confirm") {
    card.append(
      el("h1", { className: "join-title" }, "Confirm your email"),
      el(
        "p",
        { className: "join-lede" },
        `Enter the verification code we sent to ${state.email}.`,
      ),
    );
    if (state.error) {
      card.append(el("div", { className: "alert alert-error" }, state.error));
    }
    if (state.notice) {
      card.append(el("div", { className: "alert alert-ok" }, state.notice));
    }

    const form = el("form", { className: "login-form" }) as HTMLFormElement;
    const codeIn = el("input", {
      type: "text",
      name: "code",
      className: "input",
      required: "true",
      autocomplete: "one-time-code",
      placeholder: "123456",
    }) as HTMLInputElement;
    form.append(
      el("label", { className: "field" }, el("span", null, "Verification code"), codeIn),
    );
    const submit = el(
      "button",
      {
        type: "submit",
        className: "btn-primary",
        disabled: state.busy ? "true" : undefined,
      },
      state.busy ? "Confirming…" : "Confirm email",
    ) as HTMLButtonElement;
    form.append(submit);
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      void (async () => {
        render(root, { ...state, busy: true, error: undefined });
        try {
          await api.confirmSignup({ email: state.email, code: codeIn.value.trim() });
          render(root, { step: "done", email: state.email });
        } catch (err) {
          render(root, {
            ...state,
            busy: false,
            error: err instanceof ApiError ? err.message : String(err),
          });
        }
      })();
    });
    card.append(form);

    const resend = el(
      "button",
      {
        type: "button",
        className: "btn-ghost btn-sm",
        style: "margin-top:12px;",
        disabled: state.busy ? "true" : undefined,
      },
      "Resend code",
    ) as HTMLButtonElement;
    resend.addEventListener("click", () => {
      void (async () => {
        try {
          await api.resendConfirmation(state.email);
          render(root, {
            ...state,
            notice: "If confirmation is still pending, a new code was sent.",
            error: undefined,
          });
        } catch (err) {
          render(root, {
            ...state,
            error: err instanceof ApiError ? err.message : String(err),
          });
        }
      })();
    });
    card.append(resend);
    page.append(card);
    root.append(page);
    return;
  }

  // form step
  card.append(
    el("p", { className: "join-kicker" }, "Open signup · BYOK platform"),
    el("h1", { className: "join-title" }, "Create your account"),
    el(
      "p",
      { className: "join-lede" },
      "Sign up free, verify email, then subscribe ($5/mo or $50/yr) and connect your provider keys.",
    ),
  );
  if (state.error) {
    card.append(el("div", { className: "alert alert-error" }, state.error));
  }

  const form = el("form", { className: "login-form" }) as HTMLFormElement;
  const emailIn = el("input", {
    type: "email",
    name: "email",
    className: "input",
    required: "true",
    autocomplete: "email",
  }) as HTMLInputElement;
  const passIn = el("input", {
    type: "password",
    name: "password",
    className: "input",
    required: "true",
    autocomplete: "new-password",
    minlength: "12",
  }) as HTMLInputElement;
  const promoIn = el("input", {
    type: "text",
    name: "promo",
    className: "input",
    value: state.promo,
    autocomplete: "off",
    placeholder: "Optional",
  }) as HTMLInputElement;
  const refIn = el("input", {
    type: "text",
    name: "ref",
    className: "input",
    value: state.ref,
    autocomplete: "off",
    placeholder: "Optional",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, el("span", null, "Email"), emailIn),
    el(
      "label",
      { className: "field" },
      el("span", null, "Password (12+ chars)"),
      withShowPassword(passIn),
    ),
    el("label", { className: "field" }, el("span", null, "Promo code"), promoIn),
    el("label", { className: "field" }, el("span", null, "Referral code"), refIn),
  );

  const submit = el(
    "button",
    {
      type: "submit",
      className: "btn-primary",
      disabled: state.busy ? "true" : undefined,
    },
    state.busy ? "Creating…" : "Create account",
  ) as HTMLButtonElement;
  form.append(submit);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void (async () => {
      const email = emailIn.value.trim().toLowerCase();
      const password = passIn.value;
      const promoCode = promoIn.value.trim();
      const referralCode = refIn.value.trim();
      render(root, {
        step: "form",
        ref: referralCode,
        promo: promoCode,
        busy: true,
      });
      try {
        const res = await api.signup({
          email,
          password,
          ...(promoCode ? { promoCode } : {}),
          ...(referralCode ? { referralCode } : {}),
        });
        if (res.status === "confirmed") {
          render(root, { step: "done", email });
        } else {
          render(root, {
            step: "confirm",
            email,
            ref: referralCode,
            promo: promoCode,
          });
        }
      } catch (err) {
        render(root, {
          step: "form",
          ref: referralCode,
          promo: promoCode,
          busy: false,
          error: err instanceof ApiError ? err.message : String(err),
        });
      }
    })();
  });

  card.append(form);
  card.append(
    el(
      "p",
      { className: "hint", style: "margin-top:16px;" },
      "Already have an account? ",
      el("a", { href: "/" }, "Sign in"),
      " · Prefer invite-only? ",
      el("a", { href: "/join" }, "Join waitlist"),
    ),
  );
  page.append(card);
  root.append(page);
  enhancePasswordFields(root);
}
